"use client";

// Botón flotante para reportar: problemas, sugerencias, quejas y pedidos de
// producto, con fotos y videos.
//
// Va flotando y en todas las pantallas a propósito. El feedback se pierde
// cuando hay que ir a buscarlo: el que encuentra un problema lo encuentra
// ADENTRO de la pantalla rota, y si tiene que salir a buscar dónde contarlo,
// no lo cuenta — se lo manda al dueño por WhatsApp, o no lo cuenta a nadie.
//
// Se puede reportar sin cuenta: en una demo la mitad todavía no se registró, y
// el que encuentra el problema más grave suele ser el que no pudo ni entrar.
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import Icon, { IconName } from "./Icon";
import { API_BASE, hasSession } from "../lib/api";

const TIPOS: { id: string; texto: string; icono: IconName; ayuda: string }[] = [
  { id: "PROBLEMA", texto: "Algo no anda", icono: "alert", ayuda: "Un botón que no responde, una pantalla en blanco, un número mal." },
  { id: "SUGERENCIA", texto: "Se me ocurre", icono: "sparkles", ayuda: "Algo que te haría la vida más fácil." },
  { id: "QUEJA", texto: "Esto molesta", icono: "x-circle", ayuda: "Algo confuso, lento o que te hace perder tiempo." },
  { id: "PRODUCTO", texto: "Falta un repuesto", icono: "package", ayuda: "Un repuesto que necesitás y no está en la tienda." },
];

const MAX_ARCHIVOS = 4;
const MAX_MB = 25;

// No estorbar en las pantallas donde el usuario está haciendo otra cosa seria
const OCULTO_EN = ["/ingresar", "/registro", "/recuperar", "/accesos", "/auth"];

export default function BotonBuzon() {
  const ruta = usePathname();
  const [abierto, setAbierto] = useState(false);
  const [tipo, setTipo] = useState<string>("");
  const [asunto, setAsunto] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [contacto, setContacto] = useState("");
  const [nombre, setNombre] = useState("");
  const [archivos, setArchivos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState("");
  const [conSesion, setConSesion] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => setConSesion(hasSession()), [abierto, ruta]);

  // Cerrar con Escape, y no dejar que la página de atrás scrollee
  useEffect(() => {
    if (!abierto) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
    document.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [abierto]);

  function cerrar() {
    setAbierto(false);
    // Se limpia solo después de un envío exitoso: si falló, lo escrito se queda
    if (listo) {
      setTipo(""); setAsunto(""); setMensaje(""); setArchivos([]); setListo("");
    }
    setError("");
  }

  function agregar(nuevos: FileList | null) {
    if (!nuevos) return;
    setError("");
    const lista = [...archivos];
    for (const f of Array.from(nuevos)) {
      if (lista.length >= MAX_ARCHIVOS) { setError(`Máximo ${MAX_ARCHIVOS} archivos.`); break; }
      if (f.size > MAX_MB * 1024 * 1024) { setError(`«${f.name}» pesa más de ${MAX_MB} MB.`); continue; }
      lista.push(f);
    }
    setArchivos(lista);
    if (input.current) input.current.value = "";
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!tipo) { setError("Elegí de qué se trata."); return; }
    setError(""); setEnviando(true);
    try {
      // FormData y no api(): van archivos, y el navegador tiene que armar el
      // Content-Type con su boundary.
      const fd = new FormData();
      fd.append("kind", tipo);
      fd.append("subject", asunto.trim());
      fd.append("message", mensaje.trim());
      fd.append("path", ruta);
      if (!conSesion) {
        if (nombre.trim()) fd.append("contactName", nombre.trim());
        if (contacto.trim()) fd.append("contactInfo", contacto.trim());
      }
      archivos.forEach((f) => fd.append("archivos", f));

      const token = typeof window !== "undefined" ? localStorage.getItem("busco.access") : null;
      const res = await fetch(`${API_BASE}/feedback`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      const cuerpo = await res.json().catch(() => null);
      if (!res.ok) {
        const m = cuerpo?.message;
        throw new Error(Array.isArray(m) ? m.join(". ") : m || "No pudimos enviarlo. Probá de nuevo.");
      }
      setListo(cuerpo.code);
      setAsunto(""); setMensaje(""); setArchivos([]); setTipo("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos enviarlo. Probá de nuevo.");
    } finally {
      setEnviando(false);
    }
  }

  if (OCULTO_EN.some((p) => ruta === p || ruta.startsWith(`${p}/`))) return null;

  return (
    <>
      {/* Sube por encima de la barra inferior en teléfono para no taparla.
          En el mapa (/buscar) sube más todavía: abajo vive el carrusel de
          técnicos y el buzón le tapaba el botón de "Ver perfil". */}
      <button
        onClick={() => setAbierto(true)}
        aria-label="Reportar un problema o dejar una sugerencia"
        className={`fixed right-3 z-40 inline-flex items-center gap-2 rounded-full bg-[#E8A23C] text-[#201404] font-bold shadow-[0_6px_24px_rgba(0,0,0,0.45)] hover:brightness-105 active:scale-95 transition h-14 px-4 sm:px-5 ${
          ruta.startsWith("/buscar")
            ? "bottom-[calc(17.5rem+env(safe-area-inset-bottom))] sm:bottom-[17.5rem]"
            : "bottom-[calc(4.75rem+env(safe-area-inset-bottom))] sm:bottom-6"
        }`}
      >
        <Icon name="chat" size={21} strokeWidth={2} />
        <span className="hidden sm:inline text-[14px]">¿Algo para contarnos?</span>
      </button>

      {!abierto ? null : (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          onClick={(e) => { if (e.target === e.currentTarget) cerrar(); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Contanos"
            className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/12 bg-[#1A2123] text-[#EDF1EF]"
          >
            {listo ? (
              <div className="p-6 text-center">
                <span className="w-14 h-14 rounded-2xl bg-[#5CBE8F]/15 text-[#5CBE8F] grid place-items-center mx-auto">
                  <Icon name="check-circle" size={26} />
                </span>
                <h2 className="mt-4 text-xl font-bold tracking-tight">Gracias, lo recibimos</h2>
                <p className="mt-2 text-[14px] text-[#B5C0BB] leading-relaxed">
                  Quedó registrado como <strong className="font-mono text-[#E8A23C]">{listo}</strong>.
                  {conSesion
                    ? " Te avisamos por acá cuando lo veamos."
                    : " Si dejaste tu contacto, te escribimos."}
                </p>
                <button
                  onClick={cerrar}
                  className="mt-5 w-full min-h-[44px] rounded-xl bg-[#E8A23C] text-[#201404] font-bold hover:brightness-105"
                >
                  Cerrar
                </button>
              </div>
            ) : (
              <form onSubmit={enviar} className="p-5 flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold tracking-tight">Contanos</h2>
                    <p className="mt-1 text-[13.5px] text-[#B5C0BB]">
                      Lo que nos digas define qué construimos después.
                    </p>
                  </div>
                  <button type="button" onClick={cerrar} aria-label="Cerrar"
                    className="w-10 h-10 -mr-1 -mt-1 rounded-lg text-[#808D88] hover:bg-white/6 hover:text-[#EDF1EF] grid place-items-center flex-none">
                    <Icon name="x" size={19} />
                  </button>
                </div>

                {/* Tipo primero: cambia cómo se redacta lo de abajo */}
                <div className="grid grid-cols-2 gap-2">
                  {TIPOS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTipo(t.id)}
                      className={`text-left rounded-xl border p-3 transition min-h-[44px] ${
                        tipo === t.id
                          ? "border-[#E8A23C] bg-[#E8A23C]/10"
                          : "border-white/12 bg-[#14191B] hover:bg-white/5"
                      }`}
                    >
                      <span className={`inline-flex items-center gap-1.5 font-semibold text-[13.5px] ${tipo === t.id ? "text-[#E8A23C]" : ""}`}>
                        <Icon name={t.icono} size={15} /> {t.texto}
                      </span>
                    </button>
                  ))}
                </div>
                {tipo && (
                  <p className="-mt-2 text-[12.5px] text-[#808D88]">
                    {TIPOS.find((t) => t.id === tipo)?.ayuda}
                  </p>
                )}

                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-semibold text-[#B5C0BB]">En pocas palabras</span>
                  <input
                    value={asunto} onChange={(e) => setAsunto(e.target.value)} required maxLength={140}
                    placeholder="Ej: no puedo subir la foto del equipo"
                    className="min-h-[44px] rounded-xl bg-[#101516] border border-white/15 px-3.5 text-[15px] placeholder-[#808D88] focus:border-[#E8A23C]"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-semibold text-[#B5C0BB]">Contanos más</span>
                  <textarea
                    value={mensaje} onChange={(e) => setMensaje(e.target.value)} required rows={4} maxLength={4000}
                    placeholder="¿Qué estabas haciendo? ¿Qué esperabas que pase?"
                    className="rounded-xl bg-[#101516] border border-white/15 px-3.5 py-3 text-[15px] leading-relaxed placeholder-[#808D88] focus:border-[#E8A23C] resize-y"
                  />
                </label>

                {/* Sin cuenta: pedimos cómo ubicarlo, opcional */}
                {!conSesion && (
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input
                      value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={120}
                      placeholder="Tu nombre (opcional)"
                      className="min-h-[44px] rounded-xl bg-[#101516] border border-white/15 px-3.5 text-[14px] placeholder-[#808D88] focus:border-[#E8A23C]"
                    />
                    <input
                      value={contacto} onChange={(e) => setContacto(e.target.value)} maxLength={160}
                      placeholder="Teléfono o email (opcional)"
                      className="min-h-[44px] rounded-xl bg-[#101516] border border-white/15 px-3.5 text-[14px] placeholder-[#808D88] focus:border-[#E8A23C]"
                    />
                  </div>
                )}

                {/* Una foto o un video de 5 segundos explica lo que tres párrafos no */}
                <div>
                  <button
                    type="button"
                    onClick={() => input.current?.click()}
                    className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 text-[13.5px] font-semibold text-[#B5C0BB] hover:bg-white/5"
                  >
                    <Icon name="camera" size={17} /> Agregar foto o video
                  </button>
                  <input
                    ref={input} type="file" multiple accept="image/*,video/*" capture={undefined}
                    onChange={(e) => agregar(e.target.files)} className="hidden"
                  />
                  <p className="mt-1.5 text-[12px] text-[#808D88]">
                    Hasta {MAX_ARCHIVOS} archivos, {MAX_MB} MB cada uno. Un video corto suele explicar
                    mejor que un texto largo.
                  </p>
                  {archivos.length > 0 && (
                    <ul className="mt-2 flex flex-col gap-1.5">
                      {archivos.map((f, i) => (
                        <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-[#14191B] border border-white/10 px-3 py-2">
                          <Icon name={f.type.startsWith("video") ? "gamepad" : "image"} size={15} className="text-[#808D88] flex-none" />
                          <span className="text-[12.5px] truncate flex-1">{f.name}</span>
                          <span className="text-[11.5px] text-[#808D88] flex-none tabular-nums">
                            {(f.size / 1024 / 1024).toFixed(1)} MB
                          </span>
                          <button type="button" onClick={() => setArchivos(archivos.filter((_, j) => j !== i))}
                            aria-label={`Quitar ${f.name}`}
                            className="w-8 h-8 rounded-lg grid place-items-center text-[#808D88] hover:text-[#DE705C] hover:bg-white/5 flex-none">
                            <Icon name="trash" size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {error && (
                  <p className="text-[13px] text-[#DE705C] bg-[#DE705C]/10 rounded-lg px-3.5 py-2.5">{error}</p>
                )}

                <button
                  disabled={enviando || !tipo || !asunto.trim() || !mensaje.trim()}
                  className="w-full min-h-[48px] rounded-xl bg-[#E8A23C] text-[#201404] font-bold hover:brightness-105 disabled:opacity-50"
                >
                  {enviando ? "Enviando…" : "Enviar"}
                </button>
                <p className="text-[12px] text-[#808D88] text-center -mt-1">
                  Guardamos en qué pantalla estabas, para poder encontrarlo.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
